import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {Button} from 'shells/button/button';
import {SectionLabel} from 'shells/typography/section-label';

import type {
    GetContactFormSubmissions as GetContactFormSubmissionsQuery,
    GetContactFormSubmissionsVariables as GetContactFormSubmissionsQueryVariables,
} from 'nutshell-graphql-types';

import GET_CONTACT_FORM_SUBMISSIONS from '../../entity-details/graphql/queries/get-contact-form-submissions.graphql';

import {FormSubmissionTimestamp} from './form-submission-timestamp';
import {ViewFormSubmissionModal} from './view-form-submission-modal';

import styles from './form-submissions-section.module.css';

const MAX_NUM_SHOWN = 2;

type Props = {
    contactId: string;
};

export function FormSubmissionsSection(props: Props) {
    // Check the URL for a submission to open by default
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const submissionId = urlParams.get('openFormSubmission');

    // If there is a submission to open by default, this resets the URL without refreshing the page.
    if (submissionId) {
        window.history.replaceState({}, '', window.location.pathname);
    }

    const [selectedSubmissionId, setSelectedSubmissionId] = React.useState<
        string | null | undefined
    >(submissionId);
    const [shouldShowAllSubmissions, setShouldShowAllSubmissions] = React.useState<boolean>(false);

    const {data} = useQuery<
        GetContactFormSubmissionsQuery,
        GetContactFormSubmissionsQueryVariables
    >(GET_CONTACT_FORM_SUBMISSIONS, {
        variables: {id: props.contactId},
        notifyOnNetworkStatusChange: true,
    });

    const contact = data ? data.contact : null;

    const submissions =
        contact && contact.submissions ? contact.submissions.edges.map((edge) => edge.node) : [];

    if (!contact || !submissions || !submissions.length) {
        return null;
    }

    const selectedSubmission = selectedSubmissionId
        ? submissions.find((submission) => submission.id === selectedSubmissionId)
        : undefined;

    let numHiddenSubmissions;

    if (submissions && submissions.length) {
        // Only hiding submissions if there are more than one
        if (submissions.length > MAX_NUM_SHOWN + 1) {
            numHiddenSubmissions = submissions.length - MAX_NUM_SHOWN;
        } else if (!shouldShowAllSubmissions) {
            setShouldShowAllSubmissions(true);
        }
    }

    return (
        <>
            <div className={styles['form-submissions-section']}>
                <SectionLabel>Form submissions</SectionLabel>
                {submissions
                    .filter(
                        (submission, index) => shouldShowAllSubmissions || index < MAX_NUM_SHOWN
                    )
                    .map((submission) => (
                        <div key={submission.id} className={styles.submission}>
                            <Button
                                variant='text-primary'
                                size='small'
                                // @ts-expect-error
                                onClick={() => {
                                    setSelectedSubmissionId(submission.id);
                                }}
                            >
                                {submission.formVersion.form.name}
                            </Button>
                            <FormSubmissionTimestamp submissionTime={submission.submissionTime} />
                        </div>
                    ))}
                {numHiddenSubmissions ? (
                    <Button
                        variant='text-secondary'
                        size='small'
                        // @ts-expect-error
                        onClick={() => {
                            setShouldShowAllSubmissions(
                                (prevShouldShowAllSubmissions) => !prevShouldShowAllSubmissions
                            );
                        }}
                    >
                        <i className={styles['more-submissions-container']}>
                            {!shouldShowAllSubmissions
                                ? `+${numHiddenSubmissions} more forms`
                                : 'Show less'}
                        </i>
                    </Button>
                ) : undefined}
            </div>
            {selectedSubmission ? (
                <ViewFormSubmissionModal
                    onClose={() => setSelectedSubmissionId(undefined)}
                    submitter={{
                        type: 'contacts',
                        name: contact.name,
                        htmlUrlPath: contact.htmlUrlPath,
                        initials: contact.initials,
                        avatarUrl: contact.avatarUrl,
                    }}
                    submissionTime={selectedSubmission.submissionTime}
                    submissionId={selectedSubmission.id}
                    form={{
                        __typename: 'Form',
                        id: selectedSubmission.formVersion.form.id,
                        name: selectedSubmission.formVersion.form.name,
                        htmlUrlPath: selectedSubmission.formVersion.form.htmlUrlPath,
                    }}
                />
            ) : undefined}
        </>
    );
}

export function getValueForField(
    fieldValue:
        | {
              value: string;
          }
        | null
        | undefined
        | {
              values: string[];
          }
        | null
        | undefined
        | {
              subfieldValues: any[];
          }
        | null
        | undefined
): string | Array<string> | Array<any> | null | undefined {
    if (fieldValue) {
        // @ts-expect-error
        if (fieldValue.value && typeof fieldValue.value === 'string') {
            // @ts-expect-error
            return fieldValue.value;
        } else if (
            // @ts-expect-error
            fieldValue.values &&
            // @ts-expect-error
            Array.isArray(fieldValue.values) &&
            // @ts-expect-error
            fieldValue.values.every((val) => typeof val === 'string')
        ) {
            // @ts-expect-error
            return fieldValue.values;
        } else if (
            // @ts-expect-error
            fieldValue.subfieldValues &&
            // @ts-expect-error
            Array.isArray(fieldValue.subfieldValues) &&
            // @ts-expect-error
            fieldValue.subfieldValues.every((val) => typeof val === 'object')
        ) {
            // @ts-expect-error
            return fieldValue.subfieldValues;
        }
    }

    return null;
}
